import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Paper,
  Button,
  Alert,
  Switch,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery
} from '@mui/material';
import '../../styles/section-divider.css';
import FolderIcon from '@mui/icons-material/Folder';
import { CodeIcon } from '../icons';
import ChatIcon from '@mui/icons-material/Chat';
import DescriptionIcon from '@mui/icons-material/Description';
import ListAltIcon from '@mui/icons-material/ListAlt';
import SettingsIcon from '@mui/icons-material/Settings';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import SecurityIcon from '@mui/icons-material/Security';
import ApiIcon from '@mui/icons-material/Api';
import FeaturedPlayListIcon from '@mui/icons-material/FeaturedPlayList';
import PersonIcon from '@mui/icons-material/Person';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import GroupsIcon from '@mui/icons-material/Groups';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import useSection from '../../hooks/useSection';
import useAuth from '../../hooks/useAuth';
import projectTypeService from '../../services/projectTypeService';
import sectionColorService from '../../services/sectionColorService';
import SectionOrchestratorSelector from './SectionOrchestratorSelector';

/**
 * Reusable component for displaying and selecting tasks grouped by sections
 * With a left sidebar for section navigation and vertical divider
 * 
 * @param {Object} props - Component props
 * @param {Array} props.selectedTasks - Array of task IDs that are currently selected
 * @param {Function} props.onTaskSelection - Callback function when a task is selected/deselected
 * @param {Function} props.onSave - Callback function when the save button is clicked
 * @param {boolean} props.showSaveButton - Whether to show the save button (default: true)
 * @param {boolean} props.showSkipButton - Whether to show the skip button (default: true)
 * @param {string} props.saveButtonText - Text for the save button (default: "Activate Selected Topics")
 * @param {string} props.mode - Display mode: 'selection' or 'display' (default: 'selection')
 * @param {boolean} props.hideEmptySections - Whether to hide empty sections (default: true)
 * @param {boolean} props.isDialog - Whether this component is rendered in a dialog (default: false)
 * @param {string} props.title - Optional title for the component
 * @param {string} props.projectId - ID of the project (when available)
 * @returns {React.ReactElement} Component
 */
const SectionedTaskSelector = ({
  selectedTasks = [],
  onTaskSelection,
  onSave,
  onSkip,
  showSaveButton = true,
  showSkipButton = true,
  saveButtonText = "Activate selected topics",
  mode = 'selection',
  hideEmptySections = true,
  isDialog = false,
  title,
  existingProjectTasks = [], // New prop to receive tasks that are already in the project
  projectId = null // Project ID for orchestrator association
}) => {
  // Debug log to see if projectId is being passed
  console.log(`SectionedTaskSelector initialized with projectId: ${projectId}`);

  // Store projectId in sessionStorage as last_used_project_id
  if (projectId) {
    console.log(`Storing projectId ${projectId} in sessionStorage`);
    sessionStorage.setItem('last_used_project_id', projectId);
  }
  const { sections, isLoadingSections, sectionsError, refetchSections, getAllTasks } = useSection({ activeOnly: true });
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAllSections, setShowAllSections] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [projectTypes, setProjectTypes] = useState([]);
  const [isLoadingProjectTypes, setIsLoadingProjectTypes] = useState(false);
  const [projectTypesError, setProjectTypesError] = useState(null);
  
  // Helper function to format task titles with proper capitalization
  const formatTaskTitle = (title) => {
    // Special cases that should preserve their capitalization
    const specialCases = {
      'ci/cd pipeline': 'CI/CD pipeline',
      'api': 'API',
      'prd': 'PRD',
      'apd': 'APD'
    };
    
    // Check if the entire title (lowercase) matches a special case
    const lowerTitle = title.toLowerCase();
    if (specialCases[lowerTitle]) {
      return specialCases[lowerTitle];
    }
    
    // Otherwise, capitalize first letter and lowercase the rest
    // but preserve acronyms like CI/CD within the text
    let formattedTitle = title.charAt(0).toUpperCase() + title.slice(1).toLowerCase();
    
    // Replace known acronyms within the formatted title
    formattedTitle = formattedTitle.replace(/\bci\/cd\b/gi, 'CI/CD');
    formattedTitle = formattedTitle.replace(/\bapi\b/gi, 'API');
    formattedTitle = formattedTitle.replace(/\bprd\b/gi, 'PRD');
    formattedTitle = formattedTitle.replace(/\bapd\b/gi, 'APD');
    
    return formattedTitle;
  };
  
  // Helper function to map document types to icon names
  const getIconByDocType = (docType) => {
    const iconMapping = {
      'PRD': 'DescriptionIcon',
      'APD': 'DescriptionIcon',
      'FIELD_VALIDATION': 'ListAltIcon',
      'DEV_GUIDELINES': 'CodeIcon',
      'TEST_CASE': 'ListAltIcon',
      'DESIGN_GUIDELINES': 'DesignServicesIcon',
      'API_GUIDELINES': 'ApiIcon',
      'TECH_STACK': 'SettingsIcon',
      'FLOWS': 'AccountTreeIcon',
      'SECURITY_GUIDELINES': 'SecurityIcon',
      'FEATURES': 'FeaturedPlayListIcon',
      'USER_STORIES': 'PersonIcon',
      'FINAL_PRD': 'DescriptionIcon',
      'MARKDOWN_FILE': 'DescriptionIcon',
      'VALIDATION': 'ListAltIcon',
      'WIREFRAMES': 'DesignServicesIcon',
      'USER_ROLES': 'GroupsIcon',
      'PROJECT_INFO': 'ChatIcon'
    };
    
    return iconMapping[docType] || 'FolderIcon';
  };
  
  // Use the workflowTiles as a fallback if no sections are available
  // Organized into categories matching our section structure
  const workflowCategories = {
    'Functional analysis': [
      { id: 'project-info', title: 'Project Info', icon: 'ChatIcon' },
      { id: 'features', title: 'Features', icon: 'FeaturedPlayListIcon' },
      { id: 'user-roles', title: 'User Roles', icon: 'GroupsIcon' },
      { id: 'user-stories', title: 'User Stories', icon: 'PersonIcon' },
      { id: 'flow', title: 'Process Flows', icon: 'AccountTreeIcon' },
    ],
    'Design': [
      { id: 'wireframes', title: 'Wireframes', icon: 'DesignServicesIcon' },
      { id: 'design-guidelines', title: 'Design Guidelines', icon: 'DesignServicesIcon' },
      { id: 'designs', title: 'Designs', icon: 'DesignServicesIcon' },
    ],
    'Technical': [
      { id: 'field-validations', title: 'Field Validations', icon: 'ListAltIcon' },
      { id: 'tech-stack', title: 'Technology Stack', icon: 'SettingsIcon' },
      { id: 'architecture', title: 'Architecture', icon: 'ArchitectureIcon' },
      { id: 'dev-guidelines', title: 'Development Guidelines', icon: 'CodeIcon' },
      { id: 'security-guidelines', title: 'Security Guidelines', icon: 'SecurityIcon' },
    ],
    'Testing': [
      { id: 'test-cases', title: 'Test Cases', icon: 'ListAltIcon' },
      { id: 'automated-testing', title: 'Automated Testing', icon: 'AutoAwesomeIcon' },
      { id: 'security-testing', title: 'Security Testing', icon: 'SecurityIcon' },
      { id: 'performance-testing', title: 'Performance Testing', icon: 'SpeedIcon' },
    ],
    'Infrastructure': [
      { id: 'version-control', title: 'Version Control', icon: 'HistoryIcon' },
      { id: 'cicd-pipeline', title: 'CI/CD pipeline', icon: 'SettingsEthernetIcon' },
      { id: 'deployment', title: 'Deployment', icon: 'PublishIcon' },
    ]
  };
  
  // Flatten the categories for backwards compatibility
  const workflowTiles = Object.values(workflowCategories).flat();
  
  // Helper function to get icon component by name
  const getIconComponent = (iconName) => {
    const icons = {
      'CodeIcon': CodeIcon,
      'ChatIcon': ChatIcon,
      'DescriptionIcon': DescriptionIcon,
      'ListAltIcon': ListAltIcon,
      'SettingsIcon': SettingsIcon,
      'DesignServicesIcon': DesignServicesIcon,
      'SecurityIcon': SecurityIcon,
      'ApiIcon': ApiIcon,
      'FeaturedPlayListIcon': FeaturedPlayListIcon,
      'PersonIcon': PersonIcon,
      'ArchitectureIcon': ArchitectureIcon,
      'AccountTreeIcon': AccountTreeIcon,
      'GroupsIcon': GroupsIcon,
      'FolderIcon': FolderIcon
    };
    
    const IconComponent = icons[iconName] || FolderIcon;
    return <IconComponent />;
  };
  
  // Handle task selection
  const handleTaskSelection = (taskId) => {
    console.log('SectionedTaskSelector: handleTaskSelection called with taskId:', taskId);
    console.log('SectionedTaskSelector: Current selectedTasks prop:', selectedTasks);
    if (onTaskSelection) {
      onTaskSelection(taskId);
    }
  };
  
  // Handle save button click
  const handleSave = async () => {
    try {
      setLoading(true);
      if (onSave) {
        await onSave(selectedTasks);
      }
    } catch (err) {
      setError(err.message || 'Failed to save selected tasks');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle skip button click
  const handleSkip = () => {
    if (onSkip) {
      onSkip();
    }
  };
  
  // Check if user has access to a section based on role
  const hasAccessToSection = (section) => {
    console.log(user);
    // Admin has access to all sections
    if (user?.systemRole === 'admin') return true;
    
    // Check if section has required roles and if user's role is included
    if (section.requiredRoles && section.requiredRoles.length > 0) {
      return section.requiredRoles.includes(user?.systemRole || 'user');
    }
    
    // If no required roles specified, default to accessible
    return true;
  };
  // Filter sections based on user role access
  const accessibleSections = sections.filter(section => 
    showAllSections || section.isActive && hasAccessToSection(section)
  );
  
  // Function to filter out tasks that are already selected in project
  const filterAlreadySelectedTasks = (section, alreadySelectedTasks = []) => {
    if (!section.tasks) return section;
    
    // Clone the section to avoid mutating the original
    const filteredSection = {...section};
    
    // Filter out tasks that are already in the project (if we're in selection mode)
    if (mode === 'selection') {
      // Always log, even if array is empty, for debugging
      console.log(`SectionedTaskSelector: Filtering section ${section.name || 'unnamed'} tasks`);
      console.log(`SectionedTaskSelector: Tasks to be filtered out:`, alreadySelectedTasks);
      
      if (alreadySelectedTasks.length > 0) {
        // Create a Set for more efficient lookups
        const alreadySelectedTasksSet = new Set(alreadySelectedTasks);
        
        // For already selected tasks, we completely filter them out from the UI
        // so they don't appear at all in the task selector
        filteredSection.tasks = section.tasks.filter(task => {
          // IMPORTANT: Prioritize _id field first since that's what backend provides
          const taskId = task._id || task.id || task.task_id || task.taskId;
          
          // Check all possible ID fields against the already selected set
          const isAlreadySelected = alreadySelectedTasksSet.has(taskId) || 
                                   alreadySelectedTasksSet.has(task.id) ||
                                   alreadySelectedTasksSet.has(task._id) ||
                                   alreadySelectedTasksSet.has(task.task_id) ||
                                   alreadySelectedTasksSet.has(task.taskId);
          if (isAlreadySelected) {
            console.log(`SectionedTaskSelector: Filtering out task ${taskId} - ${task.title || 'no title'}`);
          }
          return !isAlreadySelected;
        });
        
        // Log the tasks that remain after filtering
        console.log(`SectionedTaskSelector: Section ${section.name || 'unnamed'} after filtering:`, 
          filteredSection.tasks.map(t => `${t.id} - ${t.title || 'no title'}`));
      } else {
        console.log(`SectionedTaskSelector: No tasks to filter out for section ${section.name || 'unnamed'}`);
      }
    }
    
    return filteredSection;
  };
  
  // Check if we have valid sections, otherwise use fallback
  const noValidSections = !accessibleSections || accessibleSections.length === 0 || 
                          accessibleSections.every(s => !s.tasks || s.tasks.length === 0);
  
  // Section descriptions are now imported from sectionColors.js

  // Create fallback section structure if needed
  const fallbackSections = noValidSections ? 
    // First check if we have project types from the database
    projectTypes && projectTypes.length > 0 ?
      // Group document types by workflow steps if available
      projectTypes[0].workflowSteps && projectTypes[0].workflowSteps.length > 0 ?
        // Create a section for each workflow step
        projectTypes[0].workflowSteps.map((step, stepIndex) => ({
          _id: `step-${stepIndex}`,
          name: step.name,
          description: step.description || '',
          tasks: projectTypes[0].documentTypes
            .filter(docType => step.documentTypes.includes(docType.type))
            .map((docType, docIndex) => ({
              id: docType.type.toLowerCase().replace(/_/g, '-'),
              title: docType.displayName,
              icon: getIconByDocType(docType.type),
              order: docIndex + 1
            }))
        }))
        :
        // If no workflow steps, create a section for each category in workflowCategories
        Object.entries(workflowCategories).map(([categoryName, categoryTasks], categoryIndex) => ({
          _id: `category-${categoryIndex}`,
          name: categoryName,
          description: sectionColorService.getSectionDescription(categoryName) || '',
          tasks: categoryTasks.map((task, taskIndex) => ({
            id: task.id,
            title: task.title,
            icon: task.icon,
            order: taskIndex + 1
          }))
        }))
    :
    // If no project types, use the hardcoded workflowCategories
    Object.entries(workflowCategories).map(([categoryName, categoryTasks], categoryIndex) => ({
      _id: `category-${categoryIndex}`,
      name: categoryName,
      description: sectionColorService.getSectionDescription(categoryName) || '',
      tasks: categoryTasks.map((task, taskIndex) => ({
        id: task.id,
        title: task.title,
        icon: task.icon,
        order: taskIndex + 1
      }))
    }))
    : accessibleSections;
    
  // Apply filtering to remove tasks that are already in the project (but not in current selection)
  const filteredSections = mode === 'selection' ? 
    fallbackSections.map(section => filterAlreadySelectedTasks(section, existingProjectTasks))
    : fallbackSections;
  
  // Fetch project types
  useEffect(() => {
    const fetchProjectTypes = async () => {
      try {
        setIsLoadingProjectTypes(true);
        const types = await projectTypeService.getProjectTypes();
        console.log('Fetched project types:', types);
        setProjectTypes(types);
        setProjectTypesError(null);
      } catch (err) {
        console.error('Error fetching project types:', err);
        setProjectTypesError(err);
      } finally {
        setIsLoadingProjectTypes(false);
      }
    };
    
    fetchProjectTypes();
  }, []);

  // Set first section as active if none is selected
  useEffect(() => {
    if (filteredSections && filteredSections.length > 0 && !activeSection) {
      setActiveSection(filteredSections[0]._id);
    }
  }, [filteredSections, activeSection]);
  
  // Debug: Log all tasks when component renders or selection changes
  useEffect(() => {
    if (mode === 'selection') {
      console.log('=== SECTIONED TASK SELECTOR STATE ===');
      console.log('Currently selected tasks prop:', selectedTasks);
      console.log('Number of selected tasks:', selectedTasks.length);
      
      // Check if all task IDs are unique
      const allTaskIds = [];
      filteredSections.forEach(section => {
        section.tasks?.forEach(task => {
          allTaskIds.push(task.id);
        });
      });
      
      const uniqueTaskIds = [...new Set(allTaskIds)];
      console.log('Total tasks:', allTaskIds.length);
      console.log('Unique task IDs:', uniqueTaskIds.length);
      if (allTaskIds.length !== uniqueTaskIds.length) {
        console.error('WARNING: Duplicate task IDs found!');
        console.log('All IDs:', allTaskIds);
        console.log('Unique IDs:', uniqueTaskIds);
      }
      console.log('=== END SECTIONED TASK SELECTOR STATE ===');
    }
  }, [filteredSections, selectedTasks, mode]);
  
  // Count tasks by section
  const getTaskCountBySection = (sectionId) => {
    const section = filteredSections.find(s => s._id === sectionId);
    return section && section.tasks ? section.tasks.length : 0;
  };

  // Get active section object
  const getActiveSection = () => {
    return filteredSections.find(s => s._id === activeSection) || filteredSections[0];
  };
  
  // Loading state UI
  if (isLoadingSections || isLoadingProjectTypes || loading) {
    return (
      <Box sx={{ py: 5, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading available tasks...
        </Typography>
      </Box>
    );
  }
  
  // Error state UI
  if (error || sectionsError || projectTypesError) {
    return (
      <Alert severity="error" sx={{ my: 3 }}>
        {(error || sectionsError || projectTypesError)?.message || 'Failed to load tasks. Please try again.'}
      </Alert>
    );
  }
  
  // If no sections available
  if (fallbackSections.length === 0) {
    return (
      <Alert severity="info" sx={{ my: 3 }}>
        No sections or tasks available.
      </Alert>
    );
  }
  
  // Mobile view - stacked layout
  if (isMobile) {
    return (
      <Box sx={{ width: '100%' }}>
        {title && (
          <Typography variant="h6" component="h2" gutterBottom>
            {title}
          </Typography>
        )}
        
        {/* All sections with horizontal dividers - mobile view */}
        <Box sx={{ width: '100%' }}>
          {filteredSections
            // Don't filter out empty sections - show all sections even if they have no tasks
            .map((section, index, filteredArray) => (
            <React.Fragment key={section._id}>
              {/* Space automatically handled by dotted divider */}

              {/* Section as a row with name on left and tasks on right */}
              <Box 
                sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2
                }}
              >
                {/* Section header with vertical divider on right */}
                <Box sx={{ position: 'relative', pr: 3, width: '150px', ml: 3 }}>
                  {/* Vertical divider line on right */}
                  <Box 
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      bottom: index === fallbackSections.length - 1 ? 0 : '20px', // Stop before dotted line for all sections except last
                      width: '1px',
                      bgcolor: 'rgba(0, 0, 0, 0.12)'
                    }}
                  />
                  
                  <Typography 
                    variant="h5" 
                    component="h2" 
                    sx={{ 
                      fontSize: { xs: '1.1rem', md: '1.2rem' },
                      fontWeight: 600,
                      color: '#333',
                      pb: 0.5
                    }}
                  >
                    {section.name}
                  </Typography>
                  
                  {/* Orchestrator selector */}
                  <SectionOrchestratorSelector 
                    sectionId={section._id} 
                    sectionName={section.name}
                    projectId={projectId}
                  />
                </Box>
                
                {/* Tasks grid */}
                <Box sx={{ pl: 3 }}>
                  {/* Show "No topics to select" message if section has no tasks */}
                  {(!section.tasks || section.tasks.length === 0) ? (
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'left', 
                        alignItems: 'center',
                        minHeight: '150px',
                        py: 4
                      }}
                    >
                      <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                        No topics to select
                      </Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={5}>
                      {section.tasks?.map((task) => {
                        // Get the task ID consistently - prioritize id field since useSectionTasks sets it
                        const taskId = task.id || task._id || task.task_id || task.taskId;
                        const isTaskSelected = selectedTasks.includes(taskId);
                        
                        return (
                        <Grid item xs={12} sm={6} md={3} key={taskId}>
                        <Box
                          className="section-task-card"
                          sx={{ 
                            height: 140, 
                            display: 'flex', 
                            flexDirection: 'column',
                            borderRadius: 2,
                            overflow: 'hidden',
                            position: 'relative',
                            backgroundColor: isTaskSelected
                                      ? sectionColorService.getStatusColors('selected').backgroundColor
                                      : sectionColorService.getSectionBackgroundColor(section._id || `category-${index}`),
                            cursor: mode === 'selection' ? 'pointer' : 'default',
                            transition: 'all 0.2s ease',
                            boxShadow: isTaskSelected 
                              ? '0 4px 10px rgba(244, 196, 67, 0.15)' // Selected shadow
                              : '0 1px 2px rgba(0,0,0,0.03)', // Default shadow
                            '&:hover': mode === 'selection' ? {
                              backgroundColor: isTaskSelected 
                                ? sectionColorService.getStatusColors('selected').backgroundColor
                                : 'rgba(0, 0, 0, 0.05)', // Use a slight overlay for hover instead of fixed color
                              transform: 'translateY(-2px)',
                              boxShadow: isTaskSelected 
                                ? '0 6px 14px rgba(244, 196, 67, 0.2)' 
                                : '0 3px 8px rgba(0,0,0,0.06)'
                            } : {}
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log(`=== TILE CLICK EVENT ===`);
                            console.log(`Task object:`, task);
                            console.log(`Task ID fields:`, { id: task.id, _id: task._id, task_id: task.task_id, taskId: task.taskId });
                            console.log(`Computed taskId: ${taskId}`);
                            console.log(`Tile clicked: ${taskId} - ${task.title}`);
                            console.log(`Event target:`, e.target);
                            console.log(`Event currentTarget:`, e.currentTarget);
                            console.log(`Mode:`, mode);
                            if (mode === 'selection') {
                              handleTaskSelection(taskId);
                            }
                            console.log(`=== END TILE CLICK EVENT ===`);
                          }}
                        >
                          <Box 
                            sx={{ 
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: '100%',
                              width: '100%',
                              p: 2,
                              textAlign: 'center'
                            }}
                          >
                            <Box 
                              sx={{ 
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: 50,
                                height: 50,
                                borderRadius: '50%',
                                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                                mb: 1.5,
                                transition: 'all 0.2s',
                                className: 'section-icon-container'
                              }}
                            >
                              {React.cloneElement(getIconComponent(task.icon), { 
                                style: {
                                  fontSize: '2.2rem',
                                  color: sectionColorService.getSectionIconColor(section._id || `category-${index}`)
                                },
                                className: 'section-icon'
                              })}
                            </Box>
                            
                            <Typography 
                              variant="body1" 
                              component="h2" 
                              sx={{ 
                                fontSize: '1rem',
                                fontWeight: 500,
                                color: '#333333',
                                lineHeight: 1.3
                              }}
                            >
                              {formatTaskTitle(task.title)}
                            </Typography>
                          </Box>
                          
                          {/* Selection indicator */}
                          {isTaskSelected && (
                            <Box 
                              sx={{ 
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                zIndex: 2,
                                width: 20,
                                height: 20,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 28.027 28.027">
                                <path d="M28.027,14.014l-.943,1.722.469,1.906L26.2,19.059l-.044,1.963-1.68,1.017-.55,1.885-1.884.55-1.017,1.679-1.963.044-1.418,1.358-1.906-.469-1.722.943-1.721-.943-1.906.469L8.968,26.2l-1.963-.044-1.017-1.68L4.1,23.923l-.55-1.884-1.68-1.017-.044-1.963L.474,17.641l.469-1.906L0,14.014l.943-1.721L.474,10.386,1.832,8.968l.044-1.963,1.68-1.017L4.1,4.1l1.885-.55,1.016-1.68,1.963-.044L10.386.474l1.906.469L14.014,0l1.722.943L17.641.474l1.418,1.358,1.963.044,1.017,1.68,1.885.55.55,1.885,1.679,1.016L26.2,8.968l1.358,1.418-.469,1.906Z" fill="#62c148"/>
                                <path d="M289.508,319.711l-3.968-3.968,1.318-1.318,2.52,2.52,7.586-9.166,1.436,1.189Z" transform="translate(-277.955 -299.731)" fill="#fff"/>
                              </svg>
                            </Box>
                          )}
                          
                        </Box>
                      </Grid>
                    )})}
                  </Grid>
                  )}
                </Box>
              </Box>
              
              {/* Add dotted line under the tasks - matching Foundation component style */}
              {index !== filteredArray.length - 1 && (
                <div className="section-divider" />
              )}
            </React.Fragment>
          ))}
        </Box>
        
        {/* Action buttons at the bottom */}
        {mode === 'selection' && showSaveButton && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            mt: 5,
            pt: 4,
            borderTop: '1px solid',
            borderColor: 'rgba(0, 0, 0, 0.08)'
          }}>
            {showSkipButton && (
              <Button
                variant="outlined"
                onClick={handleSkip}
                sx={{ 
                  color: 'text.secondary',
                  borderColor: 'rgba(0, 0, 0, 0.15)',
                  '&:hover': {
                    borderColor: 'rgba(0, 0, 0, 0.3)',
                    bgcolor: 'rgba(0, 0, 0, 0.02)',
                  },
                  borderRadius: '8px',
                  px: 3,
                  py: 1.2,
                  textTransform: 'none',
                  fontSize: '14px'
                }}
              >
                Skip
              </Button>
            )}
            
            <Button
              variant="contained" 
              color="primary"
              onClick={handleSave}
              disabled={selectedTasks.length === 0 || loading}
              sx={{ 
                bgcolor: 'var(--color-blue)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'var(--color-blue-dark)',
                },
                borderRadius: '8px',
                px: 4,
                py: 1.2,
                textTransform: 'none',
                fontSize: '15px',
                fontWeight: 500,
                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
                minWidth: 240
              }}
            >
              {loading ? 
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={16} sx={{ color: 'white', mr: 1.5 }} /> Activating...
                </Box> : 
                selectedTasks.length > 0 
                  ? `${saveButtonText} (${selectedTasks.length})`
                  : saveButtonText
              }
            </Button>
          </Box>
        )}
      </Box>
    );
  }
  
  // Desktop view - side-by-side layout
  return (
    <Box sx={{
      width: '100%',
      // Custom scrollbar styling for SectionedTaskSelector
      '&::-webkit-scrollbar': {
        width: '8px',
        backgroundColor: 'transparent',
      },
      '&::-webkit-scrollbar-track': {
        borderRadius: '10px',
        backgroundColor: 'rgba(0, 0, 0, 0.05)',
        marginTop: '5px',
        marginBottom: '5px',
      },
      '&::-webkit-scrollbar-thumb': {
        borderRadius: '10px',
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        '&:hover': {
          backgroundColor: 'rgba(25, 118, 210, 0.4)',
        },
      },
      '&::-webkit-scrollbar-corner': {
        backgroundColor: 'transparent',
      },
    }}>
      {title && (
        <Typography variant="h6" component="h2" gutterBottom>
          {title}
        </Typography>
      )}

      <Box sx={{ width: '100%' }}>
        {/* Tasks content area */}
        <Box sx={{ width: '100%' }}>
          {/* Display all sections with horizontal dividers */}
          <Box sx={{ width: '100%' }}>
            {filteredSections
              // Don't filter out empty sections - show all sections even if they have no tasks
              .map((section, index, filteredArray) => (
              <React.Fragment key={section._id}>
                {/* Space automatically handled by dotted divider */}

                {/* Section as a row with name on left and tasks on right */}
                <Box 
                  sx={{ 
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: { xs: 2, md: 4 }
                  }}
                >
                  {/* Section header left aligned with vertical divider on right */}
                  <Box 
                    sx={{ 
                      width: { xs: '100%', md: '150px' },
                      minWidth: { md: '150px' },
                      pt: { md: 1 },
                      position: 'relative',
                      ml: { md: 3 } // Add left margin to align with dialog title
                    }}
                  >
                    {/* Vertical divider line on right */}
                    <Box 
                      sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: index === fallbackSections.length - 1 ? 0 : '20px', // Stop before dotted line for all sections except last
                        width: '1px',
                        bgcolor: 'rgba(0, 0, 0, 0.12)',
                        display: { xs: 'none', md: 'block' }
                      }}
                    />
                    
                    <Typography 
                      variant="h5" 
                      component="h2" 
                      sx={{
                        fontSize: { xs: '1.1rem', md: '1.2rem' },
                        fontWeight: 600,
                        color: '#333',
                        pb: 0.5,
                        pr: { md: 3 }
                      }}
                      className="section-header"
                    >
                      {section.name}
                    </Typography>
                    
                    {/* Orchestrator selector */}
                    <SectionOrchestratorSelector 
                      sectionId={section._id} 
                      sectionName={section.name}
                      projectId={projectId}
                    />
                  </Box>
                  
                  {/* Tasks grid on the right */}
                  <Box sx={{ flex: 1, pl: { md: 2 } }}>
                    {/* Show "No topics to select" message if section has no tasks */}
                    {(!section.tasks || section.tasks.length === 0) ? (
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'left', 
                          alignItems: 'center',
                          minHeight: '150px',
                          py: 4
                        }}
                      >
                        <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                          No topics to select
                        </Typography>
                      </Box>
                    ) : (
                      <Grid container spacing={5}>
                        {section.tasks?.map((task, taskIndex) => {
                          // Get the task ID consistently - prioritize id field since useSectionTasks sets it
                          const taskId = task.id || task._id || task.task_id || task.taskId;
                          const isTaskSelected = selectedTasks.includes(taskId);
                          
                          return (
                          <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={taskId}>
                            <Box
                              className="section-task-card"
                              sx={{ 
                                // Only display if there are tasks in this section
                                display: 'flex',
                              height: 140, 
                              width: '100%',
                              maxWidth: { xs: '180px', md: '170px', lg: '160px', xl: '160px' },
                              flexDirection: 'column',
                              borderRadius: 0, // Remove rounded corners
                              overflow: 'hidden',
                              position: 'relative',
                              backgroundColor: isTaskSelected
                                ? sectionColorService.getStatusColors('selected').backgroundColor
                                : sectionColorService.getSectionBackgroundColor(section._id || `category-${index}`),
                              cursor: mode === 'selection' ? 'pointer' : 'default',
                              transition: 'all 0.2s ease',
                              ml: 'auto',
                              mr: 'auto',
                              mb: 1,
                              border: 'none',
                              boxShadow: isTaskSelected 
                                ? '0 4px 10px rgba(244, 196, 67, 0.15)' // Selected shadow
                                : 'none',
                              '&:hover': mode === 'selection' ? {
                                transform: 'translateY(-3px)',
                                boxShadow: isTaskSelected 
                                  ? '0 6px 14px rgba(244, 196, 67, 0.2)' 
                                  : '0 4px 8px rgba(0,0,0,0.08)'
                              } : {}
                            }}
                            onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log(`=== DESKTOP TILE CLICK EVENT ===`);
                            console.log(`Task object:`, task);
                            console.log(`Task ID fields:`, { id: task.id, _id: task._id, task_id: task.task_id, taskId: task.taskId });
                            console.log(`Computed taskId: ${taskId}`);
                            console.log(`Tile clicked: ${taskId} - ${task.title}`);
                            console.log(`Event target:`, e.target);
                            console.log(`Event currentTarget:`, e.currentTarget);
                            console.log(`Mode:`, mode);
                            if (mode === 'selection') {
                              handleTaskSelection(taskId);
                            }
                            console.log(`=== END DESKTOP TILE CLICK EVENT ===`);
                          }}
                          >
                            <Box 
                              sx={{ 
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                width: '100%',
                                pt: 4,
                                pb: 3.5,
                                px: 1.5,
                                textAlign: 'center'
                              }}
                            >
                              <Box 
                                sx={{ 
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  mb: 2,
                                  height: '60px',
                                  width: '60px',
                                  borderRadius: '50%',
                                  backgroundColor: 'rgba(255,255,255,0.6)',
                                  color: (theme) => {
                                    // Use section ID for consistent colors across environments
                                    const sectionId = section._id || `category-${index}`;
                                    return isTaskSelected ? 
                                      sectionColorService.getStatusColors('selected').iconColor : 
                                      sectionColorService.getSectionIconColor(sectionId);
                                  }
                                }}
                              >
                                {React.cloneElement(getIconComponent(task.icon), { 
                                  style: {
                                    fontSize: '2.2rem',
                                    color: sectionColorService.getSectionIconColor(section._id || `category-${index}`)
                                  },
                                  className: 'section-icon'
                                })}
                              </Box>
                              
                              <Typography 
                                variant="body1" 
                                component="h2" 
                                sx={{ 
                                  fontSize: '1rem',
                                  fontWeight: 500,
                                  color: '#475969',
                                  lineHeight: 1.3,
                                  mt: 1
                                }}
                              >
                                {formatTaskTitle(task.title)}
                              </Typography>
                            </Box>
                            {/* Selection indicator */}
                            {isTaskSelected && (
                              <Box 
                                sx={{ 
                                  position: 'absolute',
                                  top: 10,
                                  right: 10,
                                  zIndex: 2
                                }}
                              >
                                <svg id="Tick" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 28.027 28.027">
                                  <path id="Path_58116" data-name="Path_58116" d="M28.027,14.014l-.943,1.722.469,1.906L26.2,19.059l-.044,1.963-1.68,1.017-.55,1.885-1.884.55-1.017,1.679-1.963.044-1.418,1.358-1.906-.469-1.722.943-1.721-.943-1.906.469L8.968,26.2l-1.963-.044-1.017-1.68L4.1,23.923l-.55-1.884-1.68-1.017-.044-1.963L.474,17.641l.469-1.906L0,14.014l.943-1.721L.474,10.386,1.832,8.968l.044-1.963,1.68-1.017L4.1,4.1l1.885-.55,1.016-1.68,1.963-.044L10.386.474l1.906.469L14.014,0l1.722.943L17.641.474l1.418,1.358,1.963.044,1.017,1.68,1.885.55.55,1.885,1.679,1.016L26.2,8.968l1.358,1.418-.469,1.906Z" fill="#62c148"></path>
                                  <path id="Path_58117" data-name="Path_58117" d="M289.508,319.711l-3.968-3.968,1.318-1.318,2.52,2.52,7.586-9.166,1.436,1.189Z" transform="translate(-277.955 -299.731)" fill="#fff"></path>
                                </svg>
                              </Box>
                            )}
                            
                          </Box>
                        </Grid>
                      )})}
                    </Grid>
                    )}
                  </Box>
                </Box>
                
                {/* Add dotted line under the tasks - matching Foundation component style */}
                {index !== filteredArray.length - 1 && (
                  <div className="section-divider" />
                )}
              </React.Fragment>
            ))}
          </Box>
          
          {/* Save/Skip buttons */}
          {mode === 'selection' && showSaveButton && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              mt: 5,
              pt: 3,
              borderTop: '1px solid',
              borderColor: 'rgba(0, 0, 0, 0.08)'
            }}>
              {showSkipButton && (
                <Button
                  variant="outlined"
                  onClick={handleSkip}
                  sx={{ 
                    color: 'text.secondary',
                    borderColor: 'rgba(0, 0, 0, 0.15)',
                    '&:hover': {
                      borderColor: 'rgba(0, 0, 0, 0.3)',
                      bgcolor: 'rgba(0, 0, 0, 0.02)',
                    },
                    borderRadius: '8px',
                    px: 3,
                    py: 1.2,
                    textTransform: 'none',
                    fontSize: '14px',
                    mr: 2
                  }}
                >
                  Skip
                </Button>
              )}
              
              <Button
                variant="contained" 
                color="primary"
                onClick={handleSave}
                disabled={selectedTasks.length === 0 || loading}
                sx={{ 
                  bgcolor: 'var(--color-blue)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'var(--color-blue-dark)',
                  },
                  borderRadius: '8px',
                  px: 4,
                  py: 1.2,
                  textTransform: 'none',
                  fontSize: '15px',
                  fontWeight: 500,
                  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
                  minWidth: 240
                }}
              >
                {loading ? 
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CircularProgress size={16} sx={{ color: 'white', mr: 1.5 }} /> Activating...
                  </Box> : 
                  selectedTasks.length > 0 
                    ? `${saveButtonText} (${selectedTasks.length})`
                    : saveButtonText
                }
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SectionedTaskSelector;